<script setup lang="ts">
import type { DocumentSign, DocumentSignFollower } from '../../../../Types/document'
import DocumentSignFollowerModal from './DocumentSignFollowerModal.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import BlockContent from '../UI/Content/BlockContent.vue'
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'
const { addSuccess } = useToast()
import { useDocumentSignApi } from '../../../../Composables/DocumentSign/useDocumentSignApi.ts'
import useToast from '../../Composables/useToast'

interface Props {
  documentSign: DocumentSign
}

const props = defineProps<Props>()
const { t } = useI18n()

const deleting = ref<boolean>(false)
const { deleteDocumentSignFollower } = useDocumentSignApi()

function addFollower(follower: DocumentSignFollower): void {
  props.documentSign.documentSignFollowers.push(follower)
}

function updateFollower(follower: DocumentSignFollower): void {
  const index = props.documentSign.documentSignFollowers.findIndex(
    (documentSignFollower) => documentSignFollower.id === follower.id,
  )

  if (index === -1) {
    return
  }

  props.documentSign.documentSignFollowers[index] = follower
}

async function removeFollower(documentSignFollower: DocumentSignFollower): Promise<void> {
  if (!documentSignFollower.id) {
    return
  }
  deleting.value = true
  try {
    await deleteDocumentSignFollower(props.documentSign.id, documentSignFollower.id)
    props.documentSign.documentSignFollowers = props.documentSign.documentSignFollowers.filter(
      (follower) => follower.id !== documentSignFollower.id,
    )
    addSuccess(t('contract.document.documentSign.follower.removeSuccess'))
  } finally {
    deleting.value = false
  }
}
</script>

<template>
  <BlockContent title-t="contract.document.documentSign.follower.title" title-level="5">
    <template #header-right>
      <DocumentSignFollowerModal :document-sign-id="props.documentSign.id" @save="addFollower($event.follower)" />
    </template>
    <table v-if="props.documentSign.documentSignFollowers.length > 0" class="table">
      <thead>
        <tr>
          <th>{{ t('contract.document.documentSign.follower.fields.name.label') }}</th>
          <th>{{ t('contract.document.documentSign.follower.fields.email.label') }}</th>
          <th>{{ t('document.table.labelAction') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="follower in props.documentSign.documentSignFollowers" :key="`document-sign-follower-${follower.id}`">
          <td>{{ follower.name }}</td>
          <td>{{ follower.email }}</td>
          <td class="table-cell-centered">
            <DocumentSignFollowerModal
              :document-sign-id="props.documentSign.id"
              :document-sign-follower="follower"
              @save="updateFollower($event.follower)"
            />
            <ButtonAll
              margin="no"
              type="danger"
              icon="trash"
              class="ml-2"
              :loading="deleting"
              @click="removeFollower(follower)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <p v-else class="text-center mt-4">
      {{ t('contract.document.documentSign.follower.empty') }}
    </p>
  </BlockContent>
</template>
